import { useDataProvider, useNotify } from 'react-admin'
import { parseUserFormToBackend, UserForm, UserFormType } from '@src/pages/User/Show/CreateOrEdit/UserForm.tsx'
import { useSubmitLoader } from '@src/logic/contexts/utils/SubmitContext.tsx'
import { useDocumentTitle } from '@src/hooks/useDocumentTitle'
import styles from './UserForm.module.scss'
import { UserF } from '@src/logic/contexts/Users/UsersFrontend.type'
import { Dialog } from '@mui/material'
import { useModal } from '@src/logic/contexts/Modal/ModalContext'
import { cn } from '@src/utils/cn'

type UserProps = {
  user: UserF
  onUpdated?: () => void
}

export const UserEdit: React.FC<UserProps> = ({ user, onUpdated }) => {
  useDocumentTitle('Edit ' + user?.fullName)
  const dataProvider = useDataProvider()
  const notify = useNotify()
  const { isFormSubmitting, setIsFormSubmitting } = useSubmitLoader()
  const { hideModal } = useModal()

  const onSubmit = async (formData: UserFormType) => {
    if (isFormSubmitting) {
      return
    }

    setIsFormSubmitting(true)

    try {
      await dataProvider.update('User', {
        id: formData.id,
        data: parseUserFormToBackend(formData),
        previousData: null,
      })

      // // Handle success case
      notify('User updated successfully', { type: 'success' })
      localStorage.removeItem(`${user?.id}-userEditForm`)
      onUpdated?.()
      clearAndClose()
    } catch (error) {
      const errorMessage = error.message || 'Something went wrong, Please try again after sometime'
      notify(errorMessage, { type: 'error' })
    } finally {
      setIsFormSubmitting(false)
    }
  }

  const clearAndClose = () => hideModal()

  const handleDialogClose = (_: unknown, reason?: 'backdropClick' | 'escapeKeyDown') => {
    if (reason === 'backdropClick') return
    clearAndClose()
  }

  return (
    <Dialog open onClose={handleDialogClose} role='dialog' aria-labelledby='edit-user-dialog-title' maxWidth='sm' fullWidth>
      <div className={styles.dialogHeader}>
        <h3 id='edit-user-dialog-title'>Edit {user?.fullName}</h3>
      </div>
      <div data-page='edit-user-modal' className={cn(styles.dialogContent)}>
        <UserForm user={user} isEdit={true} onSubmit={onSubmit} onCancel={clearAndClose} isSubmitting={isFormSubmitting}></UserForm>
      </div>
    </Dialog>
  )
}
